'use client';
import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle, XCircle, RefreshCw, RotateCcw, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface QueueCounts {
  pending: number;
  sent: number;
  failed: number;
}

interface FailedEntry {
  id: string;
  lead_name: string;
  lead_phone: string;
  template_key: string;
  drip_day: number;
  attempts: number;
  last_error: string | null;
  processed_at: string | null;
}

export default function QueueStatusWidget() {
  const [counts, setCounts] = useState<QueueCounts>({ pending: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [failedEntries, setFailedEntries] = useState<FailedEntry[]>([]);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<{ sent: number; failed: number } | null>(null);

  async function fetchCounts() {
    const supabase = createClient();

    const [pendingRes, sentRes, failedRes] = await Promise.all([
      supabase.from('drip_message_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('drip_message_queue').select('*', { count: 'exact', head: true }).eq('status', 'sent'),
      supabase.from('deleted_leads_log').select('*', { count: 'exact', head: true }),
    ]);

    setCounts({
      pending: pendingRes.count ?? 0,
      sent: sentRes.count ?? 0,
      failed: failedRes.count ?? 0,
    });
    setLastUpdated(new Date());
    setLoading(false);
  }

  async function fetchFailedEntries() {
    const supabase = createClient();
    const { data } = await supabase
      .from('drip_message_queue')
      .select('id, lead_name, lead_phone, template_key, drip_day, attempts, last_error, processed_at')
      .eq('status', 'failed')
      .order('processed_at', { ascending: false })
      .limit(20);
    setFailedEntries((data as FailedEntry[]) ?? []);
  }

  async function handleRetry() {
    setRetrying(true);
    setRetryResult(null);
    try {
      const supabase = createClient();

      // Reset all failed messages back to pending (reset attempts so they can be retried)
      await supabase
        .from('drip_message_queue')
        .update({ status: 'pending', attempts: 0, last_error: null, processed_at: null })
        .eq('status', 'failed');

      // Trigger the queue processor
      const res = await fetch('/api/twilio/process-queue', { method: 'POST' });
      if (res.ok) {
        const json = await res.json();
        setRetryResult({ sent: json.sent ?? 0, failed: json.failed ?? 0 });
      }

      await fetchCounts();
      await fetchFailedEntries();
    } catch {
      // silently ignore
    } finally {
      setRetrying(false);
    }
  }

  function handleToggleErrorLog() {
    const next = !showErrorLog;
    setShowErrorLog(next);
    if (next) fetchFailedEntries();
  }

  useEffect(() => {
    fetchCounts();

    const supabase = createClient();
    const channel = supabase
      .channel('queue_status_widget')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'drip_message_queue' }, () => {
        fetchCounts();
        if (showErrorLog) fetchFailedEntries();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = [
    {
      label: 'Pending',
      value: counts.pending,
      icon: Clock,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
    },
    {
      label: 'Sent',
      value: counts.sent,
      icon: CheckCircle,
      color: 'text-success',
      bg: 'bg-green-50',
      border: 'border-green-200',
    },
    {
      label: 'Failed',
      value: counts.failed,
      icon: XCircle,
      color: 'text-danger',
      bg: 'bg-red-50',
      border: 'border-red-200',
    },
  ];

  const total = counts.pending + counts.sent + counts.failed;
  const sentPct = total > 0 ? Math.round((counts.sent / total) * 100) : 0;
  const failedPct = total > 0 ? Math.round((counts.failed / total) * 100) : 0;
  const pendingPct = total > 0 ? Math.round((counts.pending / total) * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Queue Status</h3>
          <p className="text-xs text-muted-foreground mt-0.5">WhatsApp drip message queue</p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={fetchCounts}
            className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={13} className={`text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
          </button>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-success bg-green-50 px-2 py-1 rounded-full border border-green-200">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            Live
          </span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {stats.map((stat) => {
          const StatIcon = stat.icon;
          return (
            <div key={stat.label} className={`border ${stat.border} rounded-lg p-3 flex flex-col gap-2`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{stat.label}</span>
                <div className={`w-6 h-6 rounded-md ${stat.bg} flex items-center justify-center`}>
                  <StatIcon size={13} className={stat.color} />
                </div>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${loading ? 'opacity-40' : ''} text-foreground`}>
                {loading ? '—' : stat.value.toLocaleString()}
              </p>
            </div>
          );
        })}
      </div>

      {total === 0 && !loading && (
        <p className="text-xs text-muted-foreground text-center py-2 mb-4">No messages in queue yet</p>
      )}

      {/* Retry + Error Log actions */}
      {counts.failed > 0 && (
        <div className="border-t border-border pt-3 space-y-3">
          {/* Retry result banner */}
          {retryResult && (
            <div className="flex items-center gap-2 text-xs bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle size={13} className="text-success shrink-0" />
              <span className="text-green-700">
                Retry complete — <strong>{retryResult.sent}</strong> sent, <strong>{retryResult.failed}</strong> failed
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {/* Retry button */}
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-danger hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
            >
              <RotateCcw size={12} className={retrying ? 'animate-spin' : ''} />
              {retrying ? 'Retrying…' : `Retry ${counts.failed} Failed`}
            </button>

            {/* Toggle error log */}
            <button
              onClick={handleToggleErrorLog}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <AlertTriangle size={12} className="text-danger" />
              Error Log
              {showErrorLog ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          {/* Error log table */}
          {showErrorLog && (
            <div className="rounded-lg border border-red-100 overflow-hidden">
              <div className="bg-red-50 px-3 py-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-red-700">Failed Deliveries (last 20)</span>
                <button
                  onClick={fetchFailedEntries}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  <RefreshCw size={11} />
                </button>
              </div>
              <div className="divide-y divide-red-50 max-h-56 overflow-y-auto">
                {failedEntries.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">No failed entries</p>
                ) : (
                  failedEntries.map((entry) => (
                    <div key={entry.id} className="px-3 py-2.5 bg-white hover:bg-red-50/40 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {entry.lead_name || 'Unknown'}{' '}
                            <span className="font-normal text-muted-foreground">({entry.lead_phone})</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Template: <span className="font-medium">{entry.template_key}</span> · Day {entry.drip_day} · {entry.attempts} attempt{entry.attempts !== 1 ? 's' : ''}
                          </p>
                          {entry.last_error && (
                            <p className="text-xs text-danger mt-1 break-words">{entry.last_error}</p>
                          )}
                        </div>
                        {entry.processed_at && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {new Date(entry.processed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
